/**
 * ILO Text-to-Speech Component - ENHANCED HUMAN VERSION
 * More natural, conversational voice for ILO responses
 * 
 * Improvements:
 * - Better voice selection (prefers natural-sounding voices)
 * - Optimized speech rate and pitch
 * - Enhanced text preprocessing for natural flow
 * - Proper pauses and emphasis
 */

import { Volume2, VolumeX, Pause, Play } from 'lucide-react';
import { useState, useEffect } from 'react';

/**
 * Custom hook for natural text-to-speech
 */
export function useTextToSpeech() {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);

  useEffect(() => {
    // Load and select best natural voice
    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      setVoices(availableVoices);
      
      // ENHANCED: Select most natural-sounding voice
      const preferredVoice = 
        // First choice: Samantha (macOS - very natural)
        availableVoices.find(v => v.name.includes('Samantha')) ||
        // Second: Google voices (Android/Chrome - natural)
        availableVoices.find(v => v.name.includes('Google') && v.lang.startsWith('en')) ||
        // Third: Microsoft voices - prefer Zira (Windows - natural female)
        availableVoices.find(v => v.name.includes('Zira')) ||
        availableVoices.find(v => v.name.includes('Microsoft David')) ||
        // Fourth: Any English female voice
        availableVoices.find(v => 
          v.lang.startsWith('en') && 
          (v.name.toLowerCase().includes('female') || 
           v.name.toLowerCase().includes('woman'))
        ) ||
        // Fifth: Any English voice
        availableVoices.find(v => v.lang.startsWith('en-US')) ||
        availableVoices.find(v => v.lang.startsWith('en')) ||
        // Last resort: First available
        availableVoices[0];
      
      if (preferredVoice) {
        setSelectedVoice(preferredVoice);
        console.log('🎙️ Selected voice:', preferredVoice.name, `(${preferredVoice.lang})`);
      }
    };

    loadVoices();
    
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  /**
   * ENHANCED: Clean and optimize text for natural speech
   */
  const cleanTextForSpeech = (text) => {
    return text
      // Remove citation markers
      .replace(/\[Document \d+\]/g, '')
      
      // Convert markdown to speech-friendly format
      .replace(/#{1,6}\s+(.+)/g, '$1.') // Headers → sentences
      .replace(/\*\*(.+?)\*\*/g, '$1') // Bold → normal
      .replace(/\*(.+?)\*\]/g, '$1') // Italic → normal
      .replace(/`(.+?)`/g, '$1') // Code → normal
      
      // Add natural pauses
      .replace(/\n\n+/g, '... ') // Double line breaks → long pause
      .replace(/\n/g, ', ') // Single line breaks → short pause
      
      // Improve list reading
      .replace(/^[-•]\s+/gm, '') // Remove bullet points
      .replace(/^\d+\.\s+/gm, '') // Remove number markers
      
      // Add emphasis pauses
      .replace(/:\s+/g, ': ') // After colons
      .replace(/;\s+/g, '; ') // After semicolons
      
      // Handle abbreviations naturally
      .replace(/\bCFR\b/g, 'C F R')
      .replace(/\bFDA\b/g, 'F D A')
      .replace(/\bGCP\b/g, 'G C P')
      .replace(/\bICH\b/g, 'I C H')
      .replace(/\bSOP\b/g, 'S O P')
      .replace(/\bIRB\b/g, 'I R B')
      .replace(/\bSAE\b/g, 'S A E')
      .replace(/\bAE\b/g, 'A E')
      .replace(/\bIND\b/g, 'I N D')
      .replace(/\bNDA\b/g, 'N D A')
      
      // Normalize whitespace
      .replace(/\s+/g, ' ')
      .trim();
  };

  /**
   * Speak text with natural voice
   */
  const speak = (text) => {
    if (!window.speechSynthesis) {
      console.error('Speech synthesis not supported');
      alert('Text-to-speech is not supported in your browser');
      return;
    }

    window.speechSynthesis.cancel();

    const cleanText = cleanTextForSpeech(text);

    if (!cleanText) {
      console.error('No text to speak');
      return;
    }

    const utterance = new SpeechSynthesisUtterance(cleanText);
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    
    // ENHANCED: More natural speech parameters
    utterance.rate = 0.95;   // Slightly slower than default (more natural)
    utterance.pitch = 1.0;   // Normal pitch
    utterance.volume = 1.0;  // Full volume

    utterance.onstart = () => {
      console.log('🗣️ ILO speaking...');
      setIsSpeaking(true);
      setIsPaused(false);
    };

    utterance.onend = () => {
      console.log('✅ ILO finished speaking');
      setIsSpeaking(false);
      setIsPaused(false);
    };

    utterance.onerror = (event) => {
      console.error('Speech error:', event);
      setIsSpeaking(false);
      setIsPaused(false);
    };

    window.speechSynthesis.speak(utterance);
  };

  const pause = () => {
    if (isSpeaking && !isPaused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  };

  const resume = () => {
    if (isSpeaking && isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    }
  };

  const stop = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    setIsPaused(false);
  };

  return {
    speak,
    pause,
    resume,
    stop,
    isSpeaking,
    isPaused,
    voices,
    selectedVoice,
    setSelectedVoice
  };
}

/**
 * Enhanced TTS Button Component
 */
export function TTSButton({ text, className = '' }) {
  const { speak, stop, pause, resume, isSpeaking, isPaused } = useTextToSpeech();

  const handlePlayPause = () => {
    if (!isSpeaking) {
      speak(text);
    } else if (isPaused) {
      resume();
    } else {
      pause();
    }
  };

  return (
    <div className={`flex gap-2 items-center ${className}`}>
      {/* Play/Pause Button */}
      <button
        onClick={handlePlayPause}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 text-blue-700 transition-all border border-blue-200 shadow-sm hover:shadow"
        title={isSpeaking ? (isPaused ? 'Resume' : 'Pause') : 'Listen to ILO'}
      >
        {isSpeaking ? (
          isPaused ? (
            <>
              <Play className="w-4 h-4" />
              <span className="text-sm font-medium">Resume</span>
            </>
          ) : (
            <>
              <Pause className="w-4 h-4 animate-pulse" />
              <span className="text-sm font-medium">Pause</span>
            </>
          )
        ) : (
          <>
            <Volume2 className="w-4 h-4" />
            <span className="text-sm font-medium">Listen to ILO</span>
          </>
        )}
      </button>

      {/* Stop Button */}
      {isSpeaking && (
        <button
          onClick={stop}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100 text-red-600 transition-all border border-red-200 shadow-sm hover:shadow"
          title="Stop"
        >
          <VolumeX className="w-4 h-4" />
          <span className="text-sm font-medium">Stop</span>
        </button>
      )}
    </div>
  );
}
